import Cookies from 'js-cookie';
import * as utils from './Utils';
import * as shopwareUtils from './Shopware';
import * as constants from 'common/constants';


export function allowCookieId() {
  let platform = utils.getEcommercePlatform();
  if (platform === 'shopware') return shopwareUtils.allowCookieId();
  return true;
}

export function allowContactIdCookie() {
  let platform = utils.getEcommercePlatform();
  if (platform === 'shopware') return shopwareUtils.allowContactIdCookie();
  return true;
}

export function allowBackInStockVisitorIdCookie() {
  let platform = utils.getEcommercePlatform();
  if (platform === 'shopware') return shopwareUtils.allowBackInStockVisitorIdCookie();
  return true;
}

export function allowReviewsUpvotedCookie() {
  let platform = utils.getEcommercePlatform();
  if (platform === 'shopware') return shopwareUtils.allowReviewsUpvotedCookie();
  return true;
}

export function allowPromptSessionCookies() {
  let platform = utils.getEcommercePlatform();
  if (platform === 'shopware') return shopwareUtils.allowPromptSessionCookies();
  return true;
}

export function allowPromptVisitorIdCookie() {
  let platform = utils.getEcommercePlatform();
  if (platform === 'shopware') return shopwareUtils.allowPromptVisitorIdCookie();
  return true;
}

export function allowPromptLastSeenCookie() {
  let platform = utils.getEcommercePlatform();
  if (platform === 'shopware') return shopwareUtils.allowPromptLastSeenCookie();
  return true;
}

// Follows allowContactIdCookie: the click token identifies a marketing touch, so it sits in the
// same consent class as the contact id rather than the anonymous visitor id.
export function allowClickIdCookie() {
  let platform = utils.getEcommercePlatform();
  if (platform === 'shopware') return shopwareUtils.allowContactIdCookie();
  return true;
}

//======================================
// Core Functions
//======================================
export function canSetCookie(name) {
  if (name === constants.COOKIE_ID) return allowCookieId();
  if (name === constants.COOKIE_CONTACT_ID) return allowContactIdCookie();
  if (name === constants.COOKIE_REVIEWS_UPVOTED) return allowReviewsUpvotedCookie();
  if (name === constants.COOKIE_BACKINSTOCK_VISITOR_ID) return allowBackInStockVisitorIdCookie();

  if (name.startsWith("ov_prompt_session")) return allowPromptSessionCookies();
  if (name === constants.COOKIE_PROMPT_VISITOR_ID) return allowPromptVisitorIdCookie();
  if (name === constants.COOKIE_PROMPT_LAST_SEEN) return allowPromptLastSeenCookie();
  if (name === constants.COOKIE_CLICK_ID) return allowClickIdCookie();
  return true;
}

// Capture-on-read, deliberately. The click token arrives as ?ov_click= on whatever page the
// email link lands on, but it is needed on the page where the order happens - never the same
// pageview. Putting the capture inside the getter means it works regardless of which OnVoard
// app boots on a given page, instead of depending on one app's init running everywhere: a
// merchant with Recommender but no web tracking still captures the touch.
//
// A URL value always beats the stored one. A visitor clicking a second, newer email is starting
// a new touch, and the freshest click is the one that brought them here.
export function getClickId() {
  let urlValue = "";

  try {
    urlValue = new URLSearchParams(window.location.search).get(constants.QS_CLICK_ID) || "";
  } catch(e) {
    urlValue = "";
  }

  urlValue = String(urlValue).trim();
  if (urlValue) {
    setCookie(constants.COOKIE_CLICK_ID, urlValue);
    return urlValue;
  }

  return getCookie(constants.COOKIE_CLICK_ID) || "";
}

export function getCookie(...args) {
  return utils.getCookie(...args);
}

export function setCookie(name, value, attributes={}) {
  if (!canSetCookie(name)) return;

  if (!('expires' in attributes)) {
    attributes['expires'] = constants.COOKIE_EXPIRES_DAYS;
  }

  Cookies.set(name, value, attributes);
  return Cookies.get(name, attributes);
}

export function setSessionCookie(name, value, attributes={}) {
  if (!canSetCookie(name)) return;
  Cookies.set(name, value, attributes);
  return Cookies.get(name, attributes);
}

export function getOrSetCookie(name, value, attributes={}) {
  const cookie = getCookie(name, attributes) || null;
  if (cookie) return cookie;
  if (!canSetCookie(name)) return;

  if (!('expires' in attributes)) {
    attributes['expires'] = constants.COOKIE_EXPIRES_DAYS;
  }

  setCookie(name, value, attributes);
  return getCookie(name, attributes);
}

export function removeCookie(name, attributes={}) {
  Cookies.remove(name, attributes);
}

export function getCookieInt(cookieName) {
  let value = 0;

  try {
    value = parseInt(getCookie(cookieName)) || 0;
  } catch(e) {
    value = 0;
  }

  return value;
}

export function parseCookieJson(rawValue, defaultValue) {
  let value = defaultValue;
  if (rawValue) {
    try {
      value = JSON.parse(rawValue);
    } catch(e) {
      value = defaultValue;
    }
  }

  if (!rawValue) value = defaultValue;
  return value;
}

//======================================
// Get Cookies
//======================================
export function getCookieId() {
  const cookieName = constants.COOKIE_ID;
  let cookieValue = getCookie(cookieName);
  return cookieValue;
}

export function getOrCreateCookieId() {
  let cookieValue = getCookieId();
  if (cookieValue) return cookieValue;

  const cookieName = constants.COOKIE_ID;
  if (!canSetCookie(cookieName)) return;

  cookieValue = utils.uniqueId();
  setCookie(cookieName, cookieValue);
  return cookieValue;
}

export function getContactId() {
  const cookieName = constants.COOKIE_CONTACT_ID;
  let cookieValue = getCookie(cookieName);
  return cookieValue;
}
